import { type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { AppSidebar } from './components/blocks/navigation/app-sidebar/AppSidebar';
import { cn } from './lib/utils';

/* ─── ModuleLayout ─────────────────────────────────────────────────── */

export interface ModuleLayoutProps {
  sidebar: ReactNode;
  children: ReactNode;
}

/**
 * Standard two-column layout for content modules.
 * Left: fixed-width sidebar (nav links). Right: scrollable main content.
 */
export function ModuleLayout({ sidebar, children }: ModuleLayoutProps) {
  return (
    <div className="flex h-full min-h-0">
      <div className="hidden md:flex shrink-0">{sidebar}</div>
      <main className="flex-1 min-w-0 flex flex-col overflow-y-auto">{children}</main>
    </div>
  );
}

/* ─── ModuleSidebar ────────────────────────────────────────────────── */

export interface SidebarNavItem {
  to: string;
  label: string;
  icon?: ReactNode;
  badge?: string | number;
}

export interface SidebarSection {
  title?: string;
  items: SidebarNavItem[];
}

export interface ModuleSidebarProps {
  /** Module display name shown in the sidebar header. */
  title: string;
  /** Navigation sections. */
  sections: SidebarSection[];
}

export function ModuleSidebar({ title, sections }: ModuleSidebarProps) {
  const location = useLocation();

  return (
    <AppSidebar
      className="h-full w-56"
      header={
        <span className="font-semibold text-sm truncate">{title}</span>
      }
    >
      {sections.map((section, si) => (
        <div key={si} className="mb-1">
          {section.title ? (
            <p className="px-3 py-1.5 text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
              {section.title}
            </p>
          ) : null}
          {section.items.map((item) => {
            const isExact = location.pathname === item.to;
            const isChild = item.to !== '/' && location.pathname.startsWith(item.to + '/');
            // Only match children if no sibling route is an exact match
            // (prevents /videos highlighting when /videos/channels is active)
            const siblingExact = section.items.some((s) => s.to !== item.to && location.pathname === s.to);
            const active = isExact || (isChild && !siblingExact);

            return (
              <Link
                key={item.to}
                to={item.to}
                className={cn(
                  'flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors w-full text-left',
                  active
                    ? 'bg-primary/5 text-primary hover:bg-primary/10'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted',
                )}
              >
                {item.icon ? <span className="shrink-0 [&>svg]:size-4">{item.icon}</span> : null}
                <span className="truncate flex-1">{item.label}</span>
                {item.badge != null ? (
                  <span className="ml-auto text-[11px] tabular-nums text-muted-foreground">
                    {item.badge}
                  </span>
                ) : null}
              </Link>
            );
          })}
        </div>
      ))}
    </AppSidebar>
  );
}

/* ─── ModuleBreadcrumbs ────────────────────────────────────────────── */

export interface BreadcrumbSegment {
  label: string;
  to?: string;
}

export interface ModuleBreadcrumbsProps {
  segments: BreadcrumbSegment[];
  /** Optional content rendered on the right side of the header (buttons, controls, etc.). */
  actions?: ReactNode;
  /** Optional progress value (0–100). Renders a thin bar at the bottom of the header. */
  progress?: number;
}

/**
 * Consistent breadcrumb bar for the main content area of a module.
 * Renders as a row matching the AppSidebar header height (h-14).
 */
export function ModuleBreadcrumbs({ segments, actions, progress }: ModuleBreadcrumbsProps) {
  return (
    <div className="sticky top-0 z-10 bg-background/80 backdrop-blur-sm">
      <div className="h-14 flex items-center gap-1.5 px-6 border-b text-sm text-muted-foreground">
        <div className="flex items-center gap-1.5 min-w-0 flex-1">
          {segments.map((seg, i) => {
            const isLast = i === segments.length - 1;
            return (
              <span key={i} className="inline-flex items-center gap-1.5">
                {i > 0 ? <ChevronRight size={14} className="text-muted-foreground/50" /> : null}
                {seg.to && !isLast ? (
                  <Link to={seg.to} className="hover:text-foreground transition-colors">
                    {seg.label}
                  </Link>
                ) : (
                  <span className={isLast ? 'text-foreground font-medium truncate' : ''}>
                    {seg.label}
                  </span>
                )}
              </span>
            );
          })}
        </div>
        {actions ? <div className="flex items-center gap-1 shrink-0">{actions}</div> : null}
      </div>
      {progress != null ? (
        <div className="h-0.5 bg-muted -mt-px">
          <div
            className="h-full bg-primary transition-all duration-300"
            style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
          />
        </div>
      ) : null}
    </div>
  );
}
